import type { ReactNode } from 'react';

import {
  INLINE_CODE,
  STEP_BODY,
  STEP_ITEM,
  STEP_MARKER,
  STEP_TITLE,
  STEPS_LIST,
} from './blockStyles';

interface StepProps {
  title: ReactNode;
  children?: ReactNode;
}

/**
 * 제목 안의 `백틱` 만 인라인 코드로 바꾼다.
 *
 * `title` 은 JSX **속성**이라 MDX 가 마크다운으로 파싱하지 않는다 — 그냥 두면
 * 백틱이 화면에 그대로 찍힌다. 절차 제목에 필요한 강조는 사실상 코드뿐이라
 * 마크다운 파서를 끌어오는 대신 이 한 가지만 처리한다.
 */
/** 백틱 두 개(여는 것 + 닫는 것)만 있고 내용이 없는 조각은 코드가 아니다. */
const EMPTY_CODE_LENGTH = 2;

function renderStepTitle(title: ReactNode): ReactNode {
  if (typeof title !== 'string') return title;

  return title.split(/(`[^`]+`)/g).map((part, index) =>
    part.startsWith('`') && part.endsWith('`') && part.length > EMPTY_CODE_LENGTH ? (
      <code key={index} className={INLINE_CODE}>
        {part.slice(1, -1)}
      </code>
    ) : (
      part
    ),
  );
}

/**
 * `<Steps>` 안에서만 쓴다. 번호는 CSS counter 가 매긴다 —
 * MDX 에서 중간 단계를 지워도 나머지 번호가 자동으로 당겨진다.
 */
export function Step({ title, children }: StepProps) {
  return (
    <li className={STEP_ITEM}>
      <span aria-hidden className={STEP_MARKER} />
      <div>
        <h3 className={STEP_TITLE}>{renderStepTitle(title)}</h3>
        {children && <div className={STEP_BODY}>{children}</div>}
      </div>
    </li>
  );
}

/** 순서가 있는 조작 절차. */
export function Steps({ children }: { children: ReactNode }) {
  return <ol className={STEPS_LIST}>{children}</ol>;
}
